import braintree from 'braintree-web';

const getConfig = (root, kind = 'config') => {
    const selector = `script[data-kind="${kind}"][type="application/json"]`;
    return [...root.querySelectorAll(selector)].map((script) => JSON.parse(script.textContent));
};

const paymentType = 'braintree:applepay';

// https://applepaydemo.apple.com/
Alpine.data('SevenBraintreeApplePayButton', (element) => ({
    isSupported: false,
    instance: null,

    config: getConfig(element).at(-1),
    paymentRequest: {
        total: {},
    }, // @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest

    init() {
        const canMakePayments = !!window.ApplePaySession?.canMakePayments();
        if (!this._dispatchEvent('init', { supported: canMakePayments })) return;
        if (!canMakePayments) return;

        document.addEventListener('braintree:client:init', (event) => {
            const { client } = event.detail;

            braintree.applePay.create({ client }, (error, instance) => {
                if (error) {
                    if (!this._dispatchEvent('init', { supported: false })) return;
                    this._dispatchEvent('error', { error });
                    return;
                }

                this.isSupported = true;
                this.instance = instance;
            });
        });
    },

    async submit(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const { session } = await this._createApplePaySession();
        if (this._dispatchEvent(`sessioncreated`, { id: this.config.id, session })) {
            session.begin();
        }
    },

    async _createApplePaySession() {
        if (this.config.amount) {
            this.paymentRequest.total.amount = String(this.config.amount / 100);
            this.paymentRequest.lineItems[0].amount = String(this.config.amount / 100);
        }

        if (this.config.label) {
            this.paymentRequest.total.label = this.config.label;
            this.paymentRequest.lineItems[0].label = this.config.label;
        }

        // @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest
        const { session, paymentRequest } = await new Promise((resolve, reject) => {
            const paymentRequest = {
                countryCode: this.config.country,
                currencyCode: this.config.currency,
                requiredShippingContactFields: ['postalAddress', 'phone', 'email'],
                // supportedCountries: ['US'],
                // supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
                merchantCapabilities: ['supports3DS'],
                ...(this.paymentRequest ?? {}),
                total: {
                    label: this.config.storeName,
                    ...(this.paymentRequest.total ?? {}),
                },
            };

            if (this._dispatchEvent(`createsession`, {
                id: this.config.id,
                instance: this.instance,
                paymentRequest,
                completeCreateSession: resolve,
            })) {
                resolve({
                    session: new ApplePaySession(3, this.instance.createPaymentRequest(paymentRequest)),
                    paymentRequest,
                });
            }
        });

        this._dispatchEvent(`sessioncreated`, {
            id: this.config.id,
            instance: this.instance,
            session,
        });

        session.onshippingcontactselected = (event) => {
            const complete = this._dispatchEvent(`shippingcontactselected`, {
                session,
                paymentRequest,
                id: this.config.id,
                shippingContact: event.shippingContact,
            });

            if (complete) {
                session.completeShippingContactSelection({
                    newTotal: paymentRequest.total,
                });
            }
        };

        session.oncouponcodechanged = (event) => {
            const complete = this._dispatchEvent(`couponcodechanged`, {
                session,
                paymentRequest,
                id: this.config.id,
                couponCode: event.couponCode,
            });

            if (complete) {
                session.completeCouponCodeChange({
                    newTotal: paymentRequest.total,
                });
            }
        };

        session.onvalidatemerchant = async (event) => {
            try {
                const merchantSession = await this.instance.performValidation({
                    validationURL: event.validationURL,
                    displayName: this.config.storeName,
                });

                session.completeMerchantValidation(merchantSession);
            } catch (validationErr) {
                // @todo show an error to the user, e.g. 'Apple Pay failed to load.'
                console.error('Error validating merchant:', validationErr);
                session.abort();
            }
        };

        session.onpaymentauthorized = async (event) => {
            const { payment: { token, ...payment } } = event;

            try {
                const payload = await this.instance.tokenize({ token });
                const complete = this._dispatchEvent(`paymentauthorized`, {
                    ...payment,
                    id: this.config.id,
                    paymentToken: {
                        type: paymentType,
                        nonce: payload.nonce,
                    },
                });

                if (complete) {
                    // After you have transacted with the payload.nonce,
                    // call 'completePayment' to dismiss the Apple Pay sheet.
                    session.completePayment(ApplePaySession.STATUS_SUCCESS);
                }
            } catch (tokenizeErr) {
                this._dispatchEvent(`paymentfailed`, {
                    ...payment,
                    paymentToken: {
                        type: paymentType,
                    },
                });

                session.completePayment(ApplePaySession.STATUS_FAILURE);
            }
        };

        return { session, paymentRequest };
    },

    _dispatchEvent(name, { cancelable, bubbles, ...detail } = {}) {
        return element.dispatchEvent(new CustomEvent(`seven:braintree:applepay:${name}`, {
            detail,
            bubbles: bubbles ?? true,
            cancelable: cancelable ?? true,
        }));
    },
}));


